import { PlusIcon, TrashIcon } from "@radix-ui/react-icons";
import { useMutation, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import format from "date-fns/format";
import { NewspaperIcon, Users2Icon } from "lucide-react";

import { preventBubble } from "@/components/canvas-editor/utils";
import { Page } from "@/components/Page";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button, ButtonLink } from "@/components/ui/button";
import {
  DropdownMenu,
  MenuContent,
  MenuItem,
  MenuTrigger,
} from "@/components/ui/menu";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  SimpleTooltip,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { H1, P } from "@/components/ui/typography";
import {
  deleteSubscriberList,
  getSubscriberListsQueryOptions,
  SubscriberList,
} from "@/lib/api";

export const Route = createFileRoute("/app/_sidebar-layout/subscriber-lists/")({
  component: HomeComponent,
  loader: ({ context: { queryClient } }) => {
    void queryClient.prefetchQuery(getSubscriberListsQueryOptions());
  },
  head: () => ({ meta: [{ title: "Subscriber Lists" }] }),
});

function DeleteDialog({
  list,
  children,
}: {
  list: SubscriberList;
  children: React.ReactNode;
}) {
  const mutation = useMutation({
    mutationFn: () => deleteSubscriberList(list.id),
  });

  return (
    <Tooltip>
      <AlertDialog>
        <AlertDialogTrigger asChild>
          <TooltipTrigger asChild>{children}</TooltipTrigger>
        </AlertDialogTrigger>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Delete &ldquo;{list.display_name}&rdquo;?
            </AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete your
              feed and remove your data from our servers.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              variant="destructive"
              onClick={(e) => {
                e.preventDefault();
                mutation.mutate();
              }}
              isLoading={mutation.isPending}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
          {mutation.error && (
            <div className="text-red-normal text-sm">
              {mutation.error.message}
            </div>
          )}
        </AlertDialogContent>
      </AlertDialog>
      <TooltipContent>
        <p>Delete feed</p>
      </TooltipContent>
    </Tooltip>
  );
}

function DeleteListButton({
  list,
  disabled,
  className,
  size = "icon",
  variant = "outline",
}: {
  list: SubscriberList;
  disabled?: boolean;
  className?: string;
  size?: "icon" | "smIcon";
  variant?: "outline" | "ghost";
}) {
  return (
    <DeleteDialog list={list}>
      <Button
        variant={variant}
        size={size}
        disabled={disabled}
        className={className}
      >
        <span className="sr-only">Delete list</span>
        <TrashIcon />
      </Button>
    </DeleteDialog>
  );
}

function ListTypeBadge({
  listType,
}: {
  listType: SubscriberList["list_type"];
}) {
  return listType === "memberships" ? (
    <Badge className="pl-2">Membership</Badge>
  ) : (
    <Badge className="pl-2" variant="destructive">
      Blocklist
    </Badge>
  );
}

const columns: ColumnDef<SubscriberList>[] = [
  {
    accessorKey: "list_type",
    header: "Type",
    size: 60,
    maxSize: 60,
    cell: ({ row }) => <ListTypeBadge listType={row.original.list_type} />,
  },
  {
    accessorKey: "display_name",
    header: "Name",
  },
  {
    accessorKey: "algorithms",
    header: "Feeds",
    cell: ({ row }) => (
      <div className="flex flex-wrap gap-2">
        {row.original.algorithms.map((feed) => (
          <Link
            key={`tag-${feed.id}`}
            className="text-xs rounded bg-slate-action w-fit py-1 px-2"
            to="/app/feed/$feedId/view"
            params={{ feedId: `${feed.id}` }}
            {...preventBubble}
          >
            {feed.display_name}
          </Link>
        ))}
      </div>
    ),
  },
  {
    accessorKey: "subscribers",
    header: "Users",
    cell: ({ row }) => (
      <span className="text-slate-dim">
        {Intl.NumberFormat("en-us").format(row.original.subscribers.length)}
      </span>
    ),
  },
  {
    accessorKey: "created_at",
    header: "Created",
    cell: ({ row }) => (
      <span className="text-slate-dim">
        {Intl.DateTimeFormat("en-us").format(new Date(row.original.created_at))}
      </span>
    ),
  },
  {
    accessorKey: "updated_at",
    header: "Updated",
    cell: ({ row }) => (
      <span className="text-slate-dim">
        {Intl.DateTimeFormat("en-us").format(new Date(row.original.created_at))}
      </span>
    ),
  },
  {
    id: "actions",
    enableHiding: false,
    header: () => {
      return (
        <span className="flex justify-end items-center">
          <span className="sr-only">Actions</span>
          <SimpleTooltip message="Create new feed">
            <ButtonLink
              variant="ghost"
              size="smIcon"
              to="/app/subscriber-lists/create"
            >
              <span className="sr-only">Create new list</span>
              <PlusIcon />
            </ButtonLink>
          </SimpleTooltip>
        </span>
      );
    },
    cell: ({ row }) => {
      let deleteButton = (
        <DeleteListButton
          list={row.original}
          disabled={row.original.algorithms.length > 0}
        />
      );

      if (row.original.algorithms.length > 0) {
        deleteButton = (
          <SimpleTooltip message="This list it attached to one or more feeds. You can't delete it until you remove the feed from the list.">
            <div>{deleteButton}</div>
          </SimpleTooltip>
        );
      }

      return (
        <span className="flex gap-2 justify-end opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity">
          <div {...preventBubble}>{deleteButton}</div>
        </span>
      );
    },
  },
];

function SubscriberListTable() {
  "use no memo";

  const navigate = useNavigate();
  const { data: lists } = useSuspenseQuery(getSubscriberListsQueryOptions());
  // eslint-disable-next-line react-hooks/incompatible-library
  const table = useReactTable({
    data: lists,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    defaultColumn: {},
  });

  const rows = table.getRowModel().rows;

  return (
    <div className="flex flex-col gap-4">
      <div className="flex-1">
        {lists.length > 0 ? (
          <>
            <div className="hidden md:block rounded-md border border-slate-6 dark:border-slatedark-6">
              <Table>
                <TableHeader>
                  {table.getHeaderGroups().map((headerGroup) => (
                    <TableRow key={headerGroup.id}>
                      {headerGroup.headers.map((header) => {
                        return (
                          <TableHead
                            key={header.id}
                            className="whitespace-pre"
                            style={{ width: header.getSize() }}
                          >
                            {header.isPlaceholder
                              ? null
                              : flexRender(
                                  header.column.columnDef.header,
                                  header.getContext(),
                                )}
                          </TableHead>
                        );
                      })}
                    </TableRow>
                  ))}
                </TableHeader>

                <TableBody>
                  {rows.map((row) => (
                    <TableRow
                      key={row.original.id}
                      data-state={row.getIsSelected() && "selected"}
                      className="group cursor-pointer"
                      onClick={() => {
                        navigate({
                          to: "/app/subscriber-lists/$listId/view",
                          params: { listId: row.original.id.toString() },
                        }).catch(console.error);
                      }}
                    >
                      {row.getVisibleCells().map((cell) => (
                        <TableCell key={cell.id} className="whitespace-pre">
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext(),
                          )}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            <div className="md:hidden rounded-lg overflow-hidden flex flex-col gap-2">
              {rows.map((row) => {
                return (
                  <div
                    key={`${row.id}-list`}
                    className="relative p-4 border border-slate-6 dark:border-slatedark-6 rounded-lg flex flex-col items-start gap-4"
                  >
                    <div className="flex flex-col gap-2 self-stretch">
                      <div className="flex items-center justify-between self-stretch gap-2">
                        <ListTypeBadge listType={row.original.list_type} />

                        <DropdownMenu>
                          <MenuTrigger asChild>
                            <Button variant="outline" size="sm">
                              Actions
                            </Button>
                          </MenuTrigger>
                          <MenuContent>
                            <MenuItem asChild>
                              <Link
                                to="/app/subscriber-lists/$listId/view"
                                params={{ listId: row.original.id.toString() }}
                              >
                                View
                              </Link>
                            </MenuItem>
                            <DeleteDialog list={row.original}>
                              <MenuItem onSelect={(e) => e.preventDefault()}>
                                Delete
                              </MenuItem>
                            </DeleteDialog>
                          </MenuContent>
                        </DropdownMenu>
                      </div>
                      <div className="font-semibold text-lg">
                        {row.original.display_name}
                      </div>
                    </div>
                    <div className="flex items-center justify-between self-stretch gap-2">
                      <div className="flex gap-3">
                        <div className="flex gap-1 items-center text-slate-dim text-sm">
                          <NewspaperIcon className="h-3 w-3" />
                          <div className="text-slate-dim">
                            {row.original.algorithms.length}{" "}
                            <span className="sr-only">feeds</span>
                          </div>
                        </div>
                        <div className="flex gap-1 items-center text-slate-dim text-sm">
                          <Users2Icon className="h-3 w-3" />
                          <div className="text-slate-dim">
                            {row.original.subscribers.length}{" "}
                            <span className="sr-only">users</span>
                          </div>
                        </div>
                      </div>
                      <div className="text-slate-dim text-sm">
                        {format(new Date(row.original.created_at), "MM/dd")}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        ) : (
          <div className="flex flex-col gap-4 sm:items-start">
            <div className="text-slate-dim">
              You haven&apos;t created any subscriber lists yet!
            </div>

            <ButtonLink to="/app/subscriber-lists/create">
              Create a new list
            </ButtonLink>
          </div>
        )}
      </div>
    </div>
  );
}

function HomeComponent() {
  return (
    <Page className="md:gap-10">
      <H1>Subscriber Lists</H1>
      <div className="flex flex-col gap-6">
        <P>
          Subscriber lists are a way to limit access to your feeds. You can
          manage a list of approved users and choose which feeds those lists
          apply to.
        </P>
        <SubscriberListTable />
      </div>
    </Page>
  );
}
